import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, FileText, Calendar, File, Download, Eye } from 'lucide-react';
import { useGasto, useDeleteGasto } from '../../hooks/useGastos';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { formatCurrency, formatDate } from '../../lib/utils';
import { CATEGORIAS_GASTO, METODOS_PAGO } from '../../types/gasto';
import { toast } from 'sonner';

const getCategoriaInfo = (categoria: string) => {
  return CATEGORIAS_GASTO.find(c => c.value === categoria) || CATEGORIAS_GASTO[0];
};

const getMetodoPagoLabel = (metodo: string) => {
  return METODOS_PAGO.find(m => m.value === metodo)?.label || metodo;
};

export default function GastoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const gastoId = id ? parseInt(id) : 0;

  const { data: gasto, isLoading } = useGasto(gastoId);
  const deleteGasto = useDeleteGasto();

  const handleDelete = async () => {
    if (!confirm(`¿Estás seguro de eliminar el gasto ${gasto?.numeroGasto}?`)) return;
    try {
      await deleteGasto.mutateAsync(gastoId);
      navigate('/gastos');
    } catch (error) {
      toast.error('Error al eliminar el gasto');
    }
  };

  const handleDownloadPDF = async () => {
    if (!gasto?.pdfUrl) return;
    
    try {
      const response = await fetch(gasto.pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${gasto.numeroGasto}-${gasto.concepto}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar el PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando gasto...</div>
      </div>
    );
  }

  if (!gasto) {
    return <div className="text-center py-8 text-red-500">Gasto no encontrado</div>;
  }

  const categoriaInfo = getCategoriaInfo(gasto.categoria);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/gastos')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-800">{gasto.numeroGasto}</h1>
              <span
                className="px-3 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: categoriaInfo?.color || '#6B7280' }}
              >
                {categoriaInfo?.label || gasto.categoria}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {gasto.concepto} • {formatDate(gasto.fecha)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/gastos/editar/${gasto.id}`)}>
            <Edit size={16} className="mr-2" />
            Editar
          </Button>
          <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDelete}>
            <Trash2 size={16} className="mr-2" />
            Eliminar
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Detalle del Gasto */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-blue-500" />
              Detalle del Gasto
            </h2>
            
            {/* Grid de 2 columnas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Columna Izquierda */}
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-500">Concepto</p>
                  <p className="font-medium text-gray-800">{gasto.concepto}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Monto</p>
                  <p className="font-bold text-red-600 text-xl">{formatCurrency(gasto.monto)}</p>
                </div>
                {gasto.descripcion && (
                  <div>
                    <p className="text-sm text-gray-500">Descripción</p>
                    <p className="text-gray-700">{gasto.descripcion}</p>
                  </div>
                )}
              </div>

              {/* Columna Derecha */}
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-500">Categoría</p>
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: categoriaInfo?.color || '#6B7280' }}
                    />
                    <span className="font-medium text-gray-800">{categoriaInfo?.label || gasto.categoria}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fecha</p>
                  <p className="font-medium text-gray-800 flex items-center gap-1">
                    <Calendar size={14} className="text-gray-400" />
                    {formatDate(gasto.fecha)}
                  </p>
                </div>
                {gasto.observaciones && (
                  <div>
                    <p className="text-sm text-gray-500">Observaciones</p>
                    <p className="text-gray-700">{gasto.observaciones}</p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Columna derecha */}
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Información</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Número</span>
                <span className="font-medium">{gasto.numeroGasto}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Método de Pago</span>
                <span className="font-medium">{getMetodoPagoLabel(gasto.metodoPago)}</span>
              </div>
              {gasto.comprobante && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Comprobante</span>
                  <span className="font-medium">{gasto.comprobante}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Usuario</span>
                <span className="font-medium">{gasto.usuarioCreador || 'Sistema'}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t">
                <span className="text-gray-500">Creado</span>
                <span className="text-xs text-gray-400">{formatDate(gasto.createdAt)}</span>
              </div>
              {gasto.updatedAt !== gasto.createdAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Actualizado</span>
                  <span className="text-xs text-gray-400">{formatDate(gasto.updatedAt)}</span>
                </div>
              )}
            </div>
          </Card>

          {/* PDF */}
          {gasto.pdfUrl && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <File size={20} className="text-blue-500" />
                Comprobante PDF
              </h2>
              <div className="flex flex-col gap-2">
                <a
                  href={gasto.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Eye size={16} />
                  Ver PDF
                </a>
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-green-50 text-green-600 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <Download size={16} />
                  Descargar PDF
                </button>
              </div>
            </Card>
          )}

          {!gasto.pdfUrl && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <File size={20} className="text-gray-400" />
                Comprobante PDF
              </h2>
              <p className="text-sm text-gray-400 text-center py-4">
                No hay comprobante adjunto
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}